import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Stack,
  Chip,
  Paper,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  Visibility,
  Assessment,
  CheckCircle,
  PlayArrow,
  Close,
  ArrowBackIos,
  ArrowForwardIos,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  color?: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, color = '#1976d2' }) => (
  <Card 
    sx={{ 
      height: '100%', 
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      }
    }}
  >
    <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 2, color }}>
        {icon}
      </Box>
      <Typography 
        variant="h6" 
        component="h3" 
        gutterBottom 
        fontWeight="bold"
        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2" 
        color="text.secondary" 
        lineHeight={1.6}
        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
      >
        {description}
      </Typography>
    </CardContent>
  </Card>
);

interface BenefitItemProps {
  title: string;
  description: string;
}

const BenefitItem: React.FC<BenefitItemProps> = ({ title, description }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
    <CheckCircle sx={{ color: '#4caf50', mr: 2, mt: 0.5, fontSize: { xs: 20, sm: 24 } }} />
    <Box>
      <Typography 
        variant="h6" 
        component="h4" 
        gutterBottom
        sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // デモ画像のデータ（実際の画像はpublicフォルダに配置）
  const demoSlides = [
    {
      title: 'ダッシュボード',
      description: '月次・年次の推移を視覚的に把握。ログイン不要で組合員に共有',
      image: '/demo/share.png'
    },
    {
      title: 'ダッシュボード明細画面',
      description: '収支の詳細を年ごとに確認',
      image: '/demo/dashboard_meisai.png'
    },
    {
      title: '共有ダッシュボードの管理',
      description: '共有ダッシュボードの作成・管理',
      image: '/demo/share_admin.png'
    },
    {
      title: 'CSVインポート',
      description: '既存データを簡単に取り込み',
      image: '/demo/import.png'
    }
  ];

  const handleGetStarted = () => {
    navigate('/login?mode=register');
  };

  const handleViewDemo = () => {
    setDemoOpen(true);
  };

  const handleCloseDemo = () => {
    setDemoOpen(false);
    setCurrentSlide(0);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % demoSlides.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + demoSlides.length) % demoSlides.length);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const features = [
    {
      icon: <Assessment sx={{ fontSize: 48 }} />,
      title: '収支の見える化',
      description: '一般会計・修繕積立金の収支をグラフで簡単に表示。月次・年次の推移を直感的に把握できます。',
      color: '#1976d2'
    },
    {
      icon: <CloudUpload sx={{ fontSize: 48 }} />,
      title: 'ファイル取込み',
      description: 'CSVファイルから収支データを簡単取込み。既存の会計データをそのまま活用できます。',
      color: '#ff9800'
    },
    {
      icon: <Visibility sx={{ fontSize: 48 }} />,
      title: '専用のダッシュボード',
      description: '専用URLでダッシュボードを共有。組合員の方はログインなしで収支状況を確認できます。',
      color: '#4caf50'
    }
  ];

  const benefits = [
    {
      title: '気軽に収支管理',
      description: 'CSVファイル取込みで面倒な入力作業は最小限に。データを読み込むだけで簡単スタート'
    },
    {
      title: 'みんなで確認',
      description: 'ログイン不要の共有URLで、組合員の皆様がいつでも収支状況を確認可能'
    },
    {
      title: '直感的な表示',
      description: 'グラフで収支の推移を見える化。数字が苦手な方でも一目で理解できます'
    },
    {
      title: '無料ではじめる',
      description: '収支の管理・見える化に料金はかかりません'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#fafafa', minHeight: '100vh' }}>
      {/* ヘッダー */}
      <AppBar position="fixed" sx={{ backgroundColor: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 1100 }}>
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6" component="div" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              マンション家計簿
            </Typography>
            <Stack direction="row" spacing={2}>
              {user ? (
                // ログイン済みユーザー
                <>
                  <Button variant="contained" onClick={handleDashboard}>
                    ダッシュボードへ
                  </Button>
                </>
              ) : (
                // 未ログインユーザー
                <>
                  <Button variant="contained" onClick={handleGetStarted}>
                    無料ではじめる
                  </Button>
                  <Button variant="outlined" onClick={handleLogin}>
                    ログイン
                  </Button>
                </>
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ヒーローセクション */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, sm: 8, md: 12 },
          pt: { xs: 12, sm: 14, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.75rem' },
              lineHeight: { xs: 1.2, sm: 1.3 }
            }}
          >
            マンションの財務を
            <br />
            簡単に見える化
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4, 
              opacity: 0.9, 
              lineHeight: 1.6,
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              px: { xs: 2, sm: 0 }
            }}
          >
            収支をグラフ化し管理費の不足を未然に予防
          </Typography>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 3 }} 
            justifyContent="center" 
            sx={{ mb: 4, px: { xs: 2, sm: 0 } }}
          >
            <Chip 
              label="CSVファイルからの取込み" 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }} 
            />
            <Chip 
              label="柔軟なグラフ表示" 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }} 
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button 
              variant="outlined" 
              size="large"
              startIcon={<PlayArrow />}
              sx={{ 
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
              onClick={handleViewDemo}
            >
              デモを見る
            </Button>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                backgroundColor: '#fff', 
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={handleGetStarted}
            >
              無料ではじめる
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* 主要機能セクション */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" color="primary">
            シンプルな機能
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 660, mx: 'auto' }}>
            難しい操作は不要。誰でも気軽に使える家計簿機能で、収支管理を簡単に
          </Typography>
        </Box>

        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gap: 4
          }}
        >
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </Box>
      </Container>

      {/* メリット・価値提案セクション */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, sm: 4, md: 6 },
              alignItems: 'center'
            }}
          >
            <Box>
              <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" color="primary">
                選ばれる理由
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                マンションの財務状況を把握できていますか？
                毎年の収支管理を気軽に、シンプルに。
                専用URLで関係者に収支状況を共有できます
              </Typography>
              
              {benefits.map((benefit, index) => (
                <BenefitItem key={index} {...benefit} />
              ))}
            </Box>

            <Box>
              <Paper 
                sx={{ 
                  p: { xs: 3, sm: 4 }, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                  border: '1px solid #e1f5fe'
                }}
              >
                <Assessment sx={{ fontSize: { xs: 60, sm: 70, md: 80 }, color: '#1976d2', mb: 2 }} />
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  fontWeight="bold" 
                  color="primary"
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}
                >
                  ログイン不要で
                  <br />
                  収支を共有
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' } }}
                >
                  専用URLを組合員に共有するだけ
                  <br />
                  みんなで収支状況を確認できます
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTAセクション */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: { xs: 4, sm: 6, md: 8 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            sx={{ 
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              px: { xs: 2, sm: 0 }
            }}
          >
            気軽に始めてみませんか？
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 4, 
              opacity: 0.9,
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              px: { xs: 2, sm: 0 }
            }}
          >
            無料アカウントで、収支管理を簡単・便利に
          </Typography>
          
          <Button 
            variant="contained"
            size="large"
            sx={{ 
              backgroundColor: '#fff',
              color: '#1976d2',
              px: { xs: 4, sm: 6 },
              py: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1rem', sm: '1.2rem' },
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
            onClick={handleGetStarted}
          >
            無料ではじめる
          </Button>
          
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
            クレジットカード不要・即座に利用開始可能
          </Typography>
        </Container>
      </Box>

      {/* フッター */}
      <Box sx={{ backgroundColor: '#263238', color: 'white', py: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'start', md: 'center' },
              gap: { xs: 1, sm: 2 }
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>
                マンション家計簿
              </Typography>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={{ xs: 1, sm: 3 }} 
                alignItems={{ xs: 'start', sm: 'center' }}
              >
                <Typography 
                  variant="body2" 
                  color="grey.400" 
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                >
                  マンション管理組合向けの収支管理システム
                </Typography>
                <Stack direction="row" spacing={{ xs: 1, sm: 2 }}>
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => navigate('/terms-of-service')}
                    sx={{ 
                      color: 'grey.400', 
                      textDecoration: 'underline',
                      fontSize: '0.8rem',
                      p: 0.5,
                      minWidth: 'auto'
                    }}
                  >
                    利用規約
                  </Button>
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => navigate('/privacy-policy')}
                    sx={{ 
                      color: 'grey.400', 
                      textDecoration: 'underline',
                      fontSize: '0.8rem',
                      p: 0.5,
                      minWidth: 'auto'
                    }}
                  >
                    プライバシーポリシー
                  </Button>
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => window.open('https://forms.gle/qokgompdhfb6xuMM6', '_blank')}
                    sx={{ 
                      color: 'grey.400', 
                      textDecoration: 'underline',
                      fontSize: '0.8rem',
                      p: 0.5,
                      minWidth: 'auto'
                    }}
                  >
                    お問い合わせ
                  </Button>

                  
                </Stack>
              </Stack>
            </Box>
            <Typography 
              variant="body2" 
              color="grey.400" 
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
            >
              © 2025 マンション家計簿. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* デモダイアログ */}
      <Dialog
        open={demoOpen}
        onClose={handleCloseDemo}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#000',
              color: '#fff',
            }
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={handleCloseDemo}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#fff',
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: 'relative', height: '80vh' }}>
            {/* スライド画像 */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
              }}
            >
              <Typography variant="h5" color="text.secondary">
                {/* 実際の画像がない場合のプレースホルダー */}
              </Typography>
              {/* 実際の実装では以下のようにします */}
              {<img 
                src={demoSlides[currentSlide].image} 
                alt={demoSlides[currentSlide].title}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />}
            </Box>

            {/* スライド情報 */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                p: 3,
              }}
            >
              <Typography variant="h5" gutterBottom>
                {demoSlides[currentSlide].title}
              </Typography>
              <Typography variant="body1">
                {demoSlides[currentSlide].description}
              </Typography>
            </Box>

            {/* ナビゲーションボタン */}
            <IconButton
              onClick={handlePrevSlide}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
              }}
            >
              <ArrowBackIos />
            </IconButton>
            <IconButton
              onClick={handleNextSlide}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
              }}
            >
              <ArrowForwardIos />
            </IconButton>

            {/* インジケーター */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
              }}
            >
              {demoSlides.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index === currentSlide ? '#fff' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                  }}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};